import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { AydinlatmaMetniCard } from '../components/settings/AydinlatmaMetniCard';
import { FirmNotificationRecipientsCard } from '../components/settings/FirmNotificationRecipientsCard';
import { ReminderSettingsCard } from '../components/settings/ReminderSettingsCard';
import { LoginAlertSettingsCard } from '../components/settings/LoginAlertSettingsCard';
import { KvkkTemplateCard } from '../components/settings/KvkkTemplateCard';
import { KvkkReminderSettingsCard } from '../components/settings/KvkkReminderSettingsCard';
import { EmailBrandingCard } from '../components/settings/EmailBrandingCard';
import { PdfLogoCard } from '../components/settings/PdfLogoCard';
import { AdvancedQrCard } from '../components/work-permits/AdvancedQr';
import { DocPageLimitsCard } from '../components/settings/DocPageLimitsCard';
import { EmailLogsCard } from '../components/settings/EmailLogsCard';
import { SystemEmailTemplatesCard } from '../components/settings/SystemEmailTemplatesCard';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Settings, CreditCard, Loader2, Shield, AlertCircle, FileText, Download } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const SystemSettingsPage = () => {
  const { user, canView, canTab } = useAuth();
  const [settings, setSettings] = useState({ tc_required: false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [downloadingReport, setDownloadingReport] = useState(false);
  const [downloadingScanReport, setDownloadingScanReport] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(API + '/system-settings');
      setSettings(response.data || { tc_required: false });
    } catch (error) {
      console.error('Ayarlar yuklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await axios.put(API + '/system-settings', { tc_required: settings.tc_required });
      setSettings(response.data);
      toast.success('Ayarlar kaydedildi');
    } catch (error) {
      const msg = error.response?.data?.detail || 'Ayarlar kaydedilemedi';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadSecurityReport = async () => {
    setDownloadingReport(true);
    try {
      const response = await axios.get(`${API}/security-report/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ISG_Guvenlik_KVKK_Raporu_${new Date().toISOString().slice(0,10)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Güvenlik raporu indirildi');
    } catch (error) {
      toast.error('Rapor indirilemedi');
    } finally {
      setDownloadingReport(false);
    }
  };

  const handleDownloadScanReport = async () => {
    setDownloadingScanReport(true);
    try {
      const response = await axios.get(`${API}/security-scan-report/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Guvenlik_Tarama_Raporu_${new Date().toISOString().slice(0,10)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Tarama raporu indirildi');
    } catch (error) {
      toast.error('Rapor indirilemedi');
    } finally {
      setDownloadingScanReport(false);
    }
  };

  if (!canView('system_settings')) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-rose-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-900">Yetkisiz Erisim</h2>
          <p className="text-slate-500 mt-2">Bu sayfaya erisim yetkiniz bulunmamaktadir.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div data-testid="system-settings-page" className="space-y-6 max-w-6xl">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Sistem Ayarlari</h1>
        <p className="text-slate-500 mt-1">Genel sistem ayarlarini yonetin</p>
      </div>

      <Tabs defaultValue={[new URLSearchParams(window.location.search).get('tab'), 'genel', 'bildirimler', 'kvkk', 'guvenlik', 'eposta-gecmisi'].find(t => t && canTab('system_settings', t))} className="space-y-6">
        <TabsList className="flex-wrap h-auto" data-testid="system-settings-tabs">
          {canTab('system_settings', 'genel') && <TabsTrigger value="genel" data-testid="settings-tab-genel">Genel</TabsTrigger>}
          {canTab('system_settings', 'bildirimler') && <TabsTrigger value="bildirimler" data-testid="settings-tab-bildirimler">Bildirimler</TabsTrigger>}
          {canTab('system_settings', 'kvkk') && <TabsTrigger value="kvkk" data-testid="settings-tab-kvkk">KVKK</TabsTrigger>}
          {canTab('system_settings', 'guvenlik') && <TabsTrigger value="guvenlik" data-testid="settings-tab-guvenlik">Güvenlik</TabsTrigger>}
          {canTab('system_settings', 'eposta-gecmisi') && <TabsTrigger value="eposta-gecmisi" data-testid="settings-tab-eposta-gecmisi">E-posta Geçmişi</TabsTrigger>}
        </TabsList>

        <TabsContent value="genel" className="space-y-6 max-w-2xl">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5" />
                T.C. Kimlik No Ayarlari
              </CardTitle>
              <CardDescription>
                Personel kayitlarinda T.C. Kimlik No zorunlulugunu yonetin
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div className="space-y-0.5">
                  <Label className="text-base font-medium">T.C. Kimlik No Zorunlu</Label>
                  <p className="text-sm text-slate-500">
                    Bu ayar aktif oldugunda yeni personel eklerken T.C. Kimlik No girilmesi zorunlu olur.
                  </p>
                </div>
                <Switch
                  checked={settings.tc_required}
                  onCheckedChange={(checked) => setSettings({ ...settings, tc_required: checked })}
                  data-testid="tc-required-switch"
                />
              </div>

              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <Shield className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-blue-800">T.C. Kimlik No Kurallari</h4>
                    <ul className="text-sm text-blue-700 mt-2 space-y-1 list-disc list-inside">
                      <li>Ayni firmada ayni T.C. Kimlik No ile birden fazla personel eklenemez</li>
                      <li>Farkli firmalarda ayni T.C. Kimlik No kullanilabilir</li>
                      <li>Mevcut personeller icin T.C. zorunlulugu uygulanmaz</li>
                      <li>T.C. Kimlik No algoritmik olarak dogrulanir</li>
                    </ul>
                  </div>
                </div>
              </div>

              <Button 
                onClick={handleSave} 
                className="w-full bg-slate-900 hover:bg-slate-800"
                disabled={saving}
                data-testid="save-settings-btn"
              >
                {saving ? (
                  <span className="flex items-center">
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Kaydediliyor...
                  </span>
                ) : (
                  <span className="flex items-center">
                    <Settings className="h-4 w-4 mr-2" />
                    Ayarlari Kaydet
                  </span>
                )}
              </Button>
            </CardContent>
          </Card>
          <DocPageLimitsCard />
        </TabsContent>

        <TabsContent value="bildirimler" className="space-y-6 max-w-2xl">
          <EmailBrandingCard />
          <PdfLogoCard />
          <AdvancedQrCard />
          <ReminderSettingsCard />
          <KvkkReminderSettingsCard />
          <SystemEmailTemplatesCard />
          <FirmNotificationRecipientsCard />
        </TabsContent>

        <TabsContent value="kvkk" className="space-y-6 max-w-2xl">
          <KvkkTemplateCard />
          <KvkkTemplateCard kind="aydinlatma" />
          <AydinlatmaMetniCard />
        </TabsContent>

        <TabsContent value="guvenlik" className="space-y-6 max-w-2xl">
          <LoginAlertSettingsCard />
          {/* Güvenlik ve KVKK Raporu */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-blue-600" />
                Güvenlik ve KVKK Raporu
              </CardTitle>
              <CardDescription>
                Sistem güvenlik önlemleri ve KVKK uyumluluk raporunu indirin
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <Shield className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-blue-800">Rapor İçeriği</h4>
                    <ul className="text-sm text-blue-700 mt-2 space-y-1 list-disc list-inside">
                      <li>Kimlik doğrulama ve yetkilendirme sistemi</li>
                      <li>Veri güvenliği ve KVKK uyumluluğu</li>
                      <li>T.C. Kimlik numarası maskeleme</li>
                      <li>Ağ ve iletişim güvenliği</li>
                      <li>Veri sızıntısı önlemleri</li>
                      <li>Güvenlik kontrol listesi</li>
                    </ul>
                  </div>
                </div>
              </div>

              <Button 
                onClick={handleDownloadSecurityReport} 
                className="w-full bg-blue-600 hover:bg-blue-700"
                disabled={downloadingReport}
                data-testid="download-security-report-btn"
              >
                {downloadingReport ? (
                  <span className="flex items-center">
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    İndiriliyor...
                  </span>
                ) : (
                  <span className="flex items-center">
                    <Download className="h-4 w-4 mr-2" />
                    Güvenlik Raporunu İndir (PDF)
                  </span>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Güvenlik Tarama Raporu */}
          <Card className="border-green-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-green-600" />
                Güvenlik Tarama Raporu
              </CardTitle>
              <CardDescription>
                OWASP Top 10 güvenlik tarama sonuçları - Tüm testler başarılı
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <Shield className="h-5 w-5 text-green-600 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-green-800">Tarama Özeti - Tüm Testler Başarılı</h4>
                    <ul className="text-sm text-green-700 mt-2 space-y-1 list-disc list-inside">
                      <li>10 güvenlik testi yapıldı - 10/10 başarılı ✓</li>
                      <li>Rate limiting aktif (brute-force koruması)</li>
                      <li>XSS filtreleme aktif</li>
                      <li>Genel güvenlik seviyesi: YÜKSEK</li>
                    </ul>
                  </div>
                </div>
              </div>

              <Button 
                onClick={handleDownloadScanReport} 
                className="w-full bg-green-600 hover:bg-green-700"
                disabled={downloadingScanReport}
                data-testid="download-scan-report-btn"
              >
                {downloadingScanReport ? (
                  <span className="flex items-center">
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    İndiriliyor...
                  </span>
                ) : (
                  <span className="flex items-center">
                    <Download className="h-4 w-4 mr-2" />
                    Tarama Raporunu İndir (PDF)
                  </span>
                )}
              </Button>
            </CardContent>
          </Card>

        </TabsContent>
        <TabsContent value="eposta-gecmisi" className="space-y-6">
          <EmailLogsCard />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SystemSettingsPage;
